import {
  JOIN_FLOW_STEPS,
  MembershipTier,
  UPGRADE_STEPS,
} from '@/lib/constants/maverick';
import { Region } from '@whoop/i18n/types/internationalization';
import { FrequentlyAskedQuestion } from 'ui/components/FAQ/FAQ';
import { rafGiftFlowFaqs, rafGiftFlowINFaqs } from './rafGiftFaqRulesConfig';
import { upgradeFaqs } from './upgradeFaqRulesConfig';
import { joinFlowFaqsForTrials } from '@/components/Faq/joinFaqRulesConfig';

interface IFaq {
  // Only ever true to simplify the logic of when the FAQ should be shown based on if a trial is available
  // showIfTrial: true -> only shows if a trial is available
  // hideIfTrial: true -> only shows if a trial is not available
  // Not including one of these means the FAQ should be shown regardless of trial availability
  showIfTrial?: true;
  hideIfTrial?: true;

  // Note: When excluding or including a country check if there is a similar question that needs to be included or excluded.
  countries?: {
    include?: Region[];
    exclude?: Region[];
  };
}
/**
 *
 * Join - Join Flow landing page
 * Membership - Join Flow Membership Payment Plan Page
 * Options - Upgrade Flow Options Page, peak life represents the tier
 */
export interface IUpgradeFaq extends IFaq {
  pages: {
    [UPGRADE_STEPS.UPGRADE]: Record<MembershipTier, boolean> | boolean;
    [UPGRADE_STEPS.OPTIONS]: Record<MembershipTier, boolean> | boolean;
    [UPGRADE_STEPS.UPCYCLE]: Record<MembershipTier, boolean> | boolean;
  };
}
export interface IJoinFaq extends IFaq {
  pages: {
    [JOIN_FLOW_STEPS.JOIN]: Record<MembershipTier, boolean> | boolean;
    [JOIN_FLOW_STEPS.MEMBERSHIP]: Record<MembershipTier, boolean> | boolean;
    [JOIN_FLOW_STEPS.FAMILY]?: Record<MembershipTier, boolean> | boolean;
    [JOIN_FLOW_STEPS.BANDS]?: Record<MembershipTier, boolean> | boolean;
  };
}

interface BuildFaqs {
  isUpgrade: boolean;
  isTrialAvailable: boolean;
  region: Region;
  page: UPGRADE_STEPS | JOIN_FLOW_STEPS;
  tier: MembershipTier;
  isRafGiftFlow?: boolean;
  shouldShowTruemedAnswer?: boolean;
}

export const buildFaqs = ({
  isUpgrade,
  isTrialAvailable,
  region,
  page,
  tier,
  isRafGiftFlow = false,
  shouldShowTruemedAnswer = false,
}: BuildFaqs) => {
  const pageKey = isUpgrade ? 'upgrade' : 'jf';
  let faqs: Record<string, IJoinFaq | IUpgradeFaq> = joinFlowFaqsForTrials;
  if (isRafGiftFlow) {
    if (region === 'IN') {
      faqs = rafGiftFlowINFaqs;
    } else {
      faqs = rafGiftFlowFaqs;
    }
  } else if (isUpgrade) {
    faqs = upgradeFaqs;
  }
  const filteredFaqs = Object.entries(faqs).map((faq, index) => {
    const [faqKey, faqValue] = faq;
    const { showIfTrial, hideIfTrial, pages, countries } = faqValue;

    const isHsaFsaEligibleFaq = faqKey === 'hsaFsaEligible';
    const showTruemedFaqAnswer = shouldShowTruemedAnswer && isHsaFsaEligibleFaq;
    let answer = `content.${pageKey}.${faqKey}.answer`;

    if (showTruemedFaqAnswer) {
      answer = `content.${pageKey}.${faqKey}.truemed-experiment-answer`;
    }

    const faqObject = {
      question: `content.${pageKey}.${faqKey}.question`,
      answer,
      order: index,
      faqKey,
    };

    // Default is all regions represented by an empty array
    const includedCountries = countries?.include || [];
    const excludedCountries = countries?.exclude || [];
    if (includedCountries.length && !includedCountries.includes(region)) {
      return null;
    }
    if (excludedCountries.length && excludedCountries.includes(region)) {
      return null;
    }

    // Using a typeof boolean check to decide if the value is a universal boolean or a tier-specific object.
    if (pages[page]) {
      // Based on the page rules show the FAQ
      const shouldShowFAQ =
        typeof pages[page] === 'boolean' ? pages[page] : pages[page][tier];

      if (shouldShowFAQ) {
        // If the FAQ is shown based on the page rules, then check if the FAQ should be shown based on the trial availability
        if (isTrialAvailable && hideIfTrial) {
          return null;
        }
        if (!isTrialAvailable && showIfTrial) {
          return null;
        }

        return faqObject;
      }
    }
  });

  return filteredFaqs.filter(
    (f) => f !== null && f !== undefined,
  ) as FrequentlyAskedQuestion[];
};
